import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitCustRow } from '../lib/api';
import { useSticky } from '../lib/sticky';
import { ErrorBox, money } from '../components/DataTable';
import { CalcNote } from '../components/CalcNote';
import { canScreen, useMe } from '../lib/me';
import { GroupEditor } from './ProfitCustTrend';

/**
 * 경영 분석 › 거래처별 — 수익TOOL 「➕거래처별수익」·「➕거래처별수익증감」·「📊담당자별」 대체(10-24).
 * 서버 `getProfitCust`(`functions/src/profit.ts` CUST_SQL) 가 이번 달·지난달을 지금 원장으로 센다(굳히지 않는다).
 * 거래처 = 묶음 이름(`profit_cust_group` — 시트 「조정」 표) · 통근 지출에는 직영차량 비용 배분이 들어간다.
 */

type Kind = '통근' | '일반' | '전체';
type Pick = 'all' | 'down' | 'up' | 'new' | 'gone';
type SortKey = 'profit' | 'rev' | 'rate' | 'diff';

interface Agg { key: string; type: string; manager: string; members: number; n: number; rev: number; exp: number; dalloc: number; profit: number }
interface Line { key: string; name: string; type: string; manager: string; members: number; cur: Agg | null; prev: Agg | null }

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const rateOf = (a: Agg | null) => (a && a.rev ? a.profit / a.rev : null);
const pct = (v: number | null) => (v === null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`);
const signed = (v: number) => `${v >= 0 ? '+' : ''}${money(v)}`;
const prevMonOf = (m: string) => { const [y, mm] = m.split('-').map(Number) as [number, number]; return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, '0')}`; };

function toAgg(r: ProfitCustRow, by: 'cust' | 'manager'): Agg {
  const rev = Number(r.rev), exp = Number(r.exp), dalloc = Number(r.dalloc);
  return {
    key: by === 'cust' ? `${r.recv_type}|${r.cust_name}` : r.manager || '(담당 없음)',
    type: r.recv_type, manager: r.manager, members: r.members, n: r.n, rev, exp, dalloc, profit: rev - exp - dalloc,
  };
}
function group(rows: ProfitCustRow[], kind: Kind, by: 'cust' | 'manager'): Map<string, Agg> {
  const m = new Map<string, Agg>();
  for (const r of rows) {
    if (kind !== '전체' && r.recv_type !== kind) continue;
    const a = toAgg(r, by);
    const o = m.get(a.key);
    if (!o) { m.set(a.key, a); continue; }
    o.rev += a.rev; o.exp += a.exp; o.dalloc += a.dalloc; o.profit += a.profit; o.n += a.n; o.members += a.members;
  }
  return m;
}

export default function ProfitCust() {
  const [months, setMonths] = useState<string[]>([]);
  const [mon, setMon] = useState('');
  const [kind, setKind] = useSticky<Kind>('profit.cust.kind', '통근');
  const [by, setBy] = useSticky<'cust' | 'manager'>('profit.cust.by', 'cust');
  const [pick, setPick] = useState<Pick>('all');
  const [sort, setSort] = useState<SortKey>('profit');
  const [q, setQ] = useState('');
  const [data, setData] = useState<{ cur: ProfitCustRow[]; prev: ProfitCustRow[]; prevMon: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { me } = useMe();
  const canGroup = canScreen(me, 'profit', 'write');
  const [editing, setEditing] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api.listProfitTrend({}).then((r) => {
      const set = new Set(r.rows.map((x) => x.mon));
      if (r.latestSalaryMon) set.add(r.latestSalaryMon);
      const ms = [...set].sort().reverse();
      setMonths(ms); setMon((m) => m || ms[0] || '');
    }).catch((e) => setError(errMsg(e)));
  }, []);
  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    api.getProfitCust({ mon }).then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mon, reload]);

  const lines = useMemo<Line[]>(() => {
    if (!data) return [];
    const cur = group(data.cur, kind, by), prev = group(data.prev, kind, by);
    const keys = new Set([...cur.keys(), ...prev.keys()]);
    return [...keys].map((k) => {
      const c = cur.get(k) ?? null, p = prev.get(k) ?? null, a = (c ?? p)!;
      return { key: k, name: by === 'cust' ? k.slice(k.indexOf('|') + 1) : k, type: a.type, manager: a.manager, members: a.members, cur: c, prev: p };
    });
  }, [data, kind, by]);
  const total = useMemo(() => lines.reduce((t, l) => {
    if (l.cur) { t.rev += l.cur.rev; t.profit += l.cur.profit; }
    if (l.prev) t.prevProfit += l.prev.profit;
    return t;
  }, { rev: 0, profit: 0, prevProfit: 0 }), [lines]);
  const count = useMemo(() => ({
    down: lines.filter((l) => l.cur && l.prev && l.cur.profit < l.prev.profit).length,
    up: lines.filter((l) => l.cur && l.prev && l.cur.profit > l.prev.profit).length,
    new: lines.filter((l) => l.cur && !l.prev).length,
    gone: lines.filter((l) => !l.cur && l.prev).length,
  }), [lines]);
  const shown = useMemo(() => {
    const diff = (l: Line) => (l.cur?.profit ?? 0) - (l.prev?.profit ?? 0);
    const word = q.trim();
    let xs = lines.filter((l) => (!word || l.name.includes(word) || l.manager.includes(word)));
    if (pick === 'down') xs = xs.filter((l) => l.cur && l.prev && diff(l) < 0);
    if (pick === 'up') xs = xs.filter((l) => l.cur && l.prev && diff(l) > 0);
    if (pick === 'new') xs = xs.filter((l) => l.cur && !l.prev);
    if (pick === 'gone') xs = xs.filter((l) => !l.cur && l.prev);
    // 감소·증가 보기는 증감 크기순 · 「증감」 머리를 누르면 많이 줄어든 것부터
    const sortBy: SortKey = pick === 'down' || pick === 'up' ? 'diff' : sort;
    const v = (l: Line) => sortBy === 'rev' ? (l.cur?.rev ?? -Infinity) : sortBy === 'rate' ? (rateOf(l.cur) ?? -Infinity) : sortBy === 'diff' ? diff(l) : (l.cur?.profit ?? -Infinity);
    const asc = sortBy === 'diff' && pick !== 'up';
    return [...xs].sort((a, b) => (asc ? v(a) - v(b) : v(b) - v(a)));
  }, [lines, pick, sort, q]);

  const head = (k: SortKey, label: string) => (
    <th className="cursor-pointer whitespace-nowrap px-2 py-1.5 text-right" onClick={() => setSort(k)}>
      {label}<span className="inline-block w-3">{sort === k ? '▾' : ''}</span>
    </th>
  );
  const chip = (k: Pick, label: string, n?: number) => (
    <button key={k} className={pick === k ? 'btn-primary' : 'btn'} onClick={() => setPick(k)}>{label}{n !== undefined ? ` ${n}` : ''}</button>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex gap-1">
          {(['통근', '일반', '전체'] as const).map((k) => <button key={k} className={kind === k ? 'btn-primary' : 'btn'} onClick={() => setKind(k)}>{k}</button>)}
        </div>
        <div className="flex gap-1">
          {([['cust', '거래처별'], ['manager', '버스인접수담당자별']] as const).map(([k, l]) => <button key={k} className={by === k ? 'btn-primary' : 'btn'} onClick={() => { setBy(k); setPick('all'); }}>{l}</button>)}
        </div>
        <input className="field w-48" placeholder={by === 'cust' ? '거래처·버스인접수담당자 찾기' : '버스인접수담당자 찾기'} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {chip('all', '전체', lines.length)}
        {chip('down', '수익 감소', count.down)}
        {chip('up', '수익 증가', count.up)}
        {by === 'cust' && chip('new', '신규', count.new)}
        {by === 'cust' && chip('gone', '이번 달 없음', count.gone)}
        <CalcNote>청구상태가 있는 접수만 셉니다(월 수익과 같은 범위). 지난달도 지금 원장으로 다시 세므로 굳힌 월 기록과 조금 다를 수 있습니다.
        계열사·캠퍼스는 시트 「조정」 표대로 한 줄로 묶었습니다.</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">
          {mon} 매출 {money(total.rev)} · 수익 {money(total.profit)}({pct(total.rev ? total.profit / total.rev : null)}) · 전월({data?.prevMon ?? prevMonOf(mon)}) 대비 <span className={total.profit - total.prevProfit < 0 ? 'text-red-700' : 'text-green-700'}>{signed(total.profit - total.prevProfit)}</span>
        </span>
      </div>
      {error && <ErrorBox message={error} />}
      {loading && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {data && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[56rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">{by === 'cust' ? '거래처' : '버스인접수담당자'}</th>
                {by === 'cust' && <th className="px-2 py-1.5 text-left">버스인접수담당자</th>}
                {kind === '전체' && by === 'cust' && <th className="px-2 py-1.5 text-left">구분</th>}
                {head('rev', '매출')}
                <th className="px-2 py-1.5 text-right">지출</th>
                {kind !== '일반' && <th className="px-2 py-1.5 text-right" title="직영차량 비용 배분 — 그 차 운행료 비중으로 접수에 나눔(통근만)">직영 배분</th>}
                {head('profit', '수익')}
                {head('rate', '수익률')}
                {by === 'manager' && <th className="px-2 py-1.5 text-right">수익 기여</th>}
                <th className="px-2 py-1.5 text-right">전월 수익</th>
                {head('diff', '증감')}
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => {
                const c = l.cur, p = l.prev, d = (c?.profit ?? 0) - (p?.profit ?? 0);
                return (
                  <tr key={l.key} className="border-b border-line/60 hover:bg-surface-soft">
                    <td className="px-2 py-1">
                      {l.name}
                      {l.members > 1 && by === 'cust' && <span className="ml-1 text-xs text-ink-faint" title="묶어 본 거래처 수(시트 조정 표)">({l.members}곳)</span>}
                      {!p && c && <span className="ml-1 rounded bg-sky-50 px-1 text-xs text-sky-700">신규</span>}
                      {!c && p && <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-ink-soft">이번 달 없음</span>}
                      {canGroup && by === 'cust' && <button className="btn ml-1 px-1.5 py-0 text-xs" onClick={() => setEditing(l.name)}>묶음</button>}
                    </td>
                    {by === 'cust' && <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{l.manager || '—'}</td>}
                    {kind === '전체' && by === 'cust' && <td className="px-2 py-1 text-ink-soft">{l.type}</td>}
                    <td className="px-2 py-1 text-right">{c ? money(c.rev) : '—'}</td>
                    <td className="px-2 py-1 text-right">{c ? money(c.exp) : '—'}</td>
                    {kind !== '일반' && <td className="px-2 py-1 text-right text-ink-soft">{c && c.dalloc ? money(c.dalloc) : ''}</td>}
                    <td className={`px-2 py-1 text-right font-medium ${c && c.profit < 0 ? 'text-red-700' : ''}`}>{c ? money(c.profit) : '—'}</td>
                    <td className="px-2 py-1 text-right">{pct(rateOf(c))}</td>
                    {by === 'manager' && <td className="px-2 py-1 text-right text-ink-soft">{c && total.profit ? pct(c.profit / total.profit) : '—'}</td>}
                    <td className="px-2 py-1 text-right text-ink-soft">{p ? money(p.profit) : '—'}</td>
                    <td className={`px-2 py-1 text-right ${d < 0 ? 'text-red-700' : d > 0 ? 'text-green-700' : 'text-ink-faint'}`}>{signed(d)}</td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={12} className="py-6 text-center text-ink-soft">해당하는 줄이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {editing && <GroupEditor cust={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setReload((n) => n + 1); }} />}
    </div>
  );
}
